/**
 * The downloadable sentence voices. Nothing here ships in the app binary: the model (shared by
 * every voice) and each voice's style table are downloaded on demand from the Haniverse worker
 * (`/voice-models/<file>`), which serves them from R2 and falls back to the pinned Hugging Face
 * source below. File names carry the content hash, so a changed file is a new name, and every
 * download is checked against `sha256`.
 *
 * Model: Kokoro-82M v1.1-zh (Apache-2.0, https://huggingface.co/hexgrad/Kokoro-82M-v1.1-zh), the
 * full-precision ONNX export from onnx-community. The int8 export the app shipped first adds a
 * metallic buzz above 7 kHz and flattens every voice (see `lib/sentence/speech/README.md`). The
 * voices pair few wrong syllables and tones (read through the app's own Mandarin front end) with
 * the most natural delivery among Kokoro's 100 Mandarin speakers, at a choice of pace.
 */

/** Hugging Face revision every source URL is pinned to. */
const KOKORO_SOURCE =
  "https://huggingface.co/onnx-community/Kokoro-82M-v1.1-zh-ONNX/resolve/6cc0f0d2ebe369a68b0df87c2b65c1af8c0ac3e3";

export const SYSTEM_SENTENCE_VOICE_ID = "system";

/**
 * The Apple-hosted asset pack (iOS 26 App Store delivery) holding every file of
 * `sentenceVoiceDownloadFiles()` at its root under its catalog name; `vpx _build-voice-asset-pack`
 * builds it. Devices with the pack use its files in place of the worker downloads.
 */
export const SENTENCE_VOICE_ASSET_PACK_ID = "sentence-voices";

/** The model file all Kokoro voices share. */
export const KOKORO_SENTENCE_VOICE_MODEL = {
  bytes: 339_369_442,
  file: "kokoro-v1.1-zh-fp32-94b97394.onnx",
  sha256: "94b973941b1852754f979be5d5e20be666d5c81d9bb886b88ae1dc85c9b895ca",
  sourceUrl: `${KOKORO_SOURCE}/onnx/model.onnx`,
} as const;

/**
 * Earlier model files a device may still hold. One keeps the voices speaking until the current
 * model has downloaded (on Wi-Fi, in the background); it is then deleted. Never offered for
 * download.
 */
export const SUPERSEDED_KOKORO_SENTENCE_VOICE_MODELS: readonly SentenceVoiceDownloadFile[] = [
  {
    bytes: 127_356_597,
    file: "kokoro-v1.1-zh-int8-a39469be.onnx",
    sha256: "a39469be791eeaa3089c1ed5e58b8731d1f2462ea0e7dae2bc44388e58f973d8",
    sourceUrl: `${KOKORO_SOURCE}/onnx/model_quantized.onnx`,
  },
];

/**
 * What separates the words of a sentence: nothing (`none`), a short gap (`space`) or a clear
 * pause (`pause`). Native speakers reading slowly mostly add pauses rather than stretching
 * syllables, so a slow pace sounds natural only with gaps.
 */
export type SentenceWordGap = "none" | "pause" | "space";

export type SentencePace = {
  /** Kokoro's speed: below 1 lengthens every syllable. */
  speed: number;
  wordGap: SentenceWordGap;
};

/** Every voice's normal pace. */
export const NORMAL_SENTENCE_PACE: SentencePace = { speed: 1, wordGap: "none" };

export type NeuralSentenceVoice = {
  bytes: number;
  /** One line for the voice picker. */
  description: string;
  file: string;
  gender: "female" | "male";
  id: string;
  name: string;
  /** Listed first and marked "Recommended" in the voice picker. */
  recommended: boolean;
  sha256: string;
  /**
   * The Slow setting for this voice, chosen by ear for each one: about 2–3 syllables a second
   * that still sounds like a person reading carefully.
   */
  slowPace: SentencePace;
  sourceUrl: string;
  /** Kokoro speaker id (`zf_…` female, `zm_…` male). */
  speaker: string;
};

export const NEURAL_SENTENCE_VOICES: readonly NeuralSentenceVoice[] = [
  {
    bytes: 522_240,
    description: "Male voice at an everyday conversational pace.",
    file: "kokoro-v1.1-zh-zm-089-f4315cea.bin",
    gender: "male",
    id: "kokoro-zm-089",
    name: "Hao",
    recommended: true,
    sha256: "f4315ceab376b4082cc9cdd8028d37f9aa7d43ad67ddb85902a247df0913ca89",
    slowPace: { speed: 0.7, wordGap: "space" },
    sourceUrl: `${KOKORO_SOURCE}/voices/zm_089.bin`,
    speaker: "zm_089",
  },
  {
    bytes: 522_240,
    description: "Female voice at an everyday conversational pace.",
    file: "kokoro-v1.1-zh-zf-086-c228ba01.bin",
    gender: "female",
    id: "kokoro-zf-086",
    name: "Lan",
    recommended: false,
    sha256: "c228ba018bf56d5de1339bde10dcbea7700797b85351e4d57ee5307ac7d3ee99",
    slowPace: { speed: 0.7, wordGap: "space" },
    sourceUrl: `${KOKORO_SOURCE}/voices/zf_086.bin`,
    speaker: "zf_086",
  },
  {
    bytes: 522_240,
    description: "Male voice at a calm, slow pace.",
    file: "kokoro-v1.1-zh-zm-081-ffc1341f.bin",
    gender: "male",
    // 慢, "slow".
    id: "kokoro-zm-081",
    name: "Man",
    recommended: false,
    sha256: "ffc1341f044c95ad8ef598079df9e3a85d81d093268ae8422848a9b81d386fd2",
    slowPace: { speed: 0.7, wordGap: "pause" },
    sourceUrl: `${KOKORO_SOURCE}/voices/zm_081.bin`,
    speaker: "zm_081",
  },
  {
    bytes: 522_240,
    description: "Male voice at an unhurried pace, easy to follow.",
    file: "kokoro-v1.1-zh-zm-100-112de52f.bin",
    gender: "male",
    id: "kokoro-zm-100",
    name: "Jun",
    recommended: false,
    sha256: "112de52f1aab3b370fd01f4e2e1d8bb37fee2725e77e14b81c802d50687e0e6e",
    slowPace: { speed: 0.65, wordGap: "none" },
    sourceUrl: `${KOKORO_SOURCE}/voices/zm_100.bin`,
    speaker: "zm_100",
  },
  {
    bytes: 522_240,
    description: "Female voice at an unhurried pace, easy to follow.",
    file: "kokoro-v1.1-zh-zf-042-71316009.bin",
    gender: "female",
    id: "kokoro-zf-042",
    name: "Mei",
    recommended: false,
    sha256: "713160091b44d962ab73e3bf66f0c2f0b9677aaf0c2f0e62faa94ed451540d1e",
    slowPace: { speed: 0.8, wordGap: "none" },
    sourceUrl: `${KOKORO_SOURCE}/voices/zf_042.bin`,
    speaker: "zf_042",
  },
];

export type SentenceVoiceId = typeof SYSTEM_SENTENCE_VOICE_ID | NeuralSentenceVoice["id"];

export function getNeuralSentenceVoice(id: string | null): NeuralSentenceVoice | undefined {
  return NEURAL_SENTENCE_VOICES.find((voice) => voice.id === id);
}

export type SentenceVoiceDownloadFile = {
  bytes: number;
  file: string;
  sha256: string;
  sourceUrl: string;
};

/** Every downloadable file: the shared model, then each voice. */
export function sentenceVoiceDownloadFiles(): SentenceVoiceDownloadFile[] {
  return [KOKORO_SENTENCE_VOICE_MODEL, ...NEURAL_SENTENCE_VOICES].map(
    ({ bytes, file, sha256, sourceUrl }) => ({ bytes, file, sha256, sourceUrl }),
  );
}

/** A downloadable file by name: the worker's allowlist and fallback map. */
export function getSentenceVoiceDownloadFile(file: string): SentenceVoiceDownloadFile | undefined {
  return sentenceVoiceDownloadFiles().find((download) => download.file === file);
}
